import { Component, ErrorInfo, ReactNode } from "react";
import { reportError } from "@/lib/errorReporter";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("ErrorBoundary caught:", error, errorInfo);
    reportError(error, {
      level: "error",
      context: { componentStack: errorInfo.componentStack?.slice(0, 1500) },
    });
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
    window.location.href = "/";
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-background flex items-center justify-center p-6">
          <div className="max-w-md w-full text-center space-y-6">
            <div className="w-16 h-16 rounded-full bg-foreground/[0.05] flex items-center justify-center mx-auto">
              <span className="text-2xl">⚠</span>
            </div>
            <div className="space-y-2">
              <h1 className="text-xl font-medium text-foreground tracking-tight">
                Qualcosa è andato storto
              </h1>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Si è verificato un errore imprevisto. Riprova o torna alla pagina principale.
              </p>
            </div>
            <button
              onClick={this.handleReset}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-foreground text-background text-sm hover:bg-foreground/90 transition-colors"
            >
              Torna alla Home
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
